import type { ReactNode } from 'react';
import Icon from '@/components/ui/Icon/Icon';
import StatusRow from '@/components/ui/StatusRow/StatusRow';
import type { ReinfoPortfolio } from '@/lib/api.types';
import type { LeadSpec, RowSpec } from '@/lib/report-content';
import { useDevMode } from '@/hooks/useDevMode';
import styles from './shared.module.css';

// Risk → status/label mapping lives in lib/risk.ts (shared with the verdict card).
// Re-exported here so the many `from './shared'` chapter imports keep working.
export { rankToStatus, rankToStatusLabel } from '@/lib/risk';

export function ChapterLead({ label, message }: LeadSpec) {
  return (
    <div className={styles.lead}>
      {label && <div className={styles.label}>{label}</div>}
      {message && <p className={styles.message}>{message}</p>}
    </div>
  );
}

// RowSpec.iconTone → 画面の CSS 変数。PDF 側は pdf/theme.ts で同じ意味の色に解決する。
const ICON_TONE_COLOR: Record<NonNullable<RowSpec['iconTone']>, string | undefined> = {
  ink: 'var(--color-sumi)',
  alert: 'var(--color-alert)',
  safe: 'var(--color-koke)',
  inherit: undefined,
};

// 章の状態行 (StatusRow) の並び。行の中身はビルダーが RowSpec で決め、ここは描くだけ。
export function RowList({ rows }: { rows: RowSpec[] }) {
  if (!rows.length) return null;
  return (
    <div>
      {rows.map((row, i) => (
        <StatusRow
          key={`${row.label}-${i}`}
          icon={<Icon name={row.icon} color={ICON_TONE_COLOR[row.iconTone ?? 'ink']} />}
          label={row.label}
          detail={row.detail}
          status={row.status}
          statusLabel={row.statusLabel}
        />
      ))}
    </div>
  );
}

export function CheckList({ items, title = '確かめたいこと' }: { items: string[]; title?: string }) {
  if (!items.length) return null;
  return (
    <div className={styles.check}>
      <h3 className={styles.checkTitle}>{title}</h3>
      <ul className={styles.checkList}>
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export function RawDetails({
  portfolio,
  label = '生データを見る (dev)',
}: {
  portfolio: ReinfoPortfolio | null | undefined;
  label?: string;
}) {
  const dev = useDevMode();
  if (!dev) return null;
  if (!portfolio) return null;
  const hasMetrics = portfolio.metrics && Object.keys(portfolio.metrics).length > 0;
  const hasLayers = portfolio.layers && portfolio.layers.length > 0;
  if (!hasMetrics && !hasLayers) return null;
  return (
    <details className={styles.details}>
      <summary>{label}</summary>
      {hasMetrics && (
        <pre className={styles.json}>
          {JSON.stringify(portfolio.metrics, null, 2)}
        </pre>
      )}
      {hasLayers && (
        <pre className={styles.json}>
          {JSON.stringify(portfolio.layers, null, 2)}
        </pre>
      )}
    </details>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className={styles.empty}>{children}</div>;
}
